import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";

import { Subscription, forkJoin } from "rxjs";
import { PortfolioService } from "src/app/pages/portfolio/portfolio.service";
import { ApiEndpoints } from "src/app/ApiEndpoints";
import { MaterialModule } from "src/app/app.materials";
import { PortfolioModel } from "src/app/shared/models/portfolio.model";

interface TradeStep {
  risk: number;
  sharpe: number;
  turnover: number;
  closeness: number;
  exp_return: number;
  description: string;
  new_weights: number[];
}

interface NextBestTradeItem {
  id_w: number;
  lambda_value: number;
  target_utility: number;
  current_utility: number;
  baseline_sharpe: number;
  trades: {
    step_1: TradeStep | null;
    step_2: TradeStep | null;
    step_3: TradeStep | null;
  };
}

export interface TradePoint {
  x: number;
  y: number;
  label: string;
  sharpe: number;
}

interface TableRow {
  key: string;
  portfolio: string;
  trades: number | string;
  expReturn: number | null;
  risk: number | null;
  sharpe: number | null;
  sharpeDelta: number | null;
  gapClosed: number | null;
  turnover: number | null;
  isBaseline: boolean;
  isSelectedOptimal: boolean;
  step: TradeStep | null;
  isEmpty?: boolean;
  emptyShortLabel?: string;
  emptyTooltip?: string;
}

interface WeightChangeRow {
  asset: string;
  before: number;
  after: number;
  change: number;
}

@Component({
  selector: "app-next-best-trade",
  templateUrl: "./next-best-trade.component.html",
  styleUrls: ["./next-best-trade.component.scss"],
  standalone: true,
  imports: [MaterialModule],
})
export class NextBestTradeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sliderVal: number = 0;
  @Input() baselineMetrics: {
    risk: number;
    expReturn: number;
    sharpe: number;
  } | null = null;
  @Input() selectedPortfolioMetrics: {
    risk: number;
    expReturn: number;
    sharpe: number;
  } | null = null;
  @Input() sliderAllocations: any[] = [];
  @Input() assetList: any[] = [];

  @Output() tradePointsChanged = new EventEmitter<TradePoint[]>();
  @Output() customTradeSaved = new EventEmitter<{
    step: TradeStep;
    optimizationData: any;
  }>();

  isLoading = false;
  hasData = false;
  isSaving = false;

  currentTrade: NextBestTradeItem | null = null;
  tableRows: TableRow[] = [];
  expandedStep: string | null = null;
  weightChanges: WeightChangeRow[] = [];

  private nbtData: NextBestTradeItem[] = [];
  private portfolioService: PortfolioService;
  private portfolioId: number | null = null;
  private portfolio: PortfolioModel | null = null;
  private portfolioSub: Subscription;

  private readonly stepDefs: Array<{
    key: "step_1" | "step_2" | "step_3";
    label: string;
    trades: number;
  }> = [
    { key: "step_1", label: "1-Trade", trades: 1 },
    { key: "step_2", label: "2-Trade", trades: 2 },
    { key: "step_3", label: "3-Trade", trades: 3 },
  ];

  constructor(private injector: Injector) {
    this.portfolioService = injector.get(PortfolioService);
  }

  ngOnInit(): void {
    this.portfolioSub = this.portfolioService.portfolio$.subscribe(
      (portfolio) => {
        if (portfolio?.id) {
          this.portfolioId = portfolio.id;
          this.portfolio = portfolio;
          this.fetchData();
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["sliderVal"] && this.nbtData.length > 0) {
      this.updateCurrentTrade();
    }
    if (
      (changes["baselineMetrics"] ||
        changes["selectedPortfolioMetrics"] ||
        changes["sliderAllocations"]) &&
      this.currentTrade
    ) {
      this.buildTableRows();
    }
  }

  ngOnDestroy(): void {
    this.portfolioSub?.unsubscribe();
  }

  fetchData(): void {
    if (!this.portfolioId) return;
    this.isLoading = true;
    this.hasData = false;

    const endpoint = ApiEndpoints.portfolioNextBestTrade.replace(
      ":id",
      String(this.portfolioId),
    );
    this.portfolioService.getRequest<any>(endpoint).subscribe({
      next: (resp: any) => {
        this.isLoading = false;
        const data: NextBestTradeItem[] =
          resp?.data ?? resp?.success?.data ?? [];
        if (data.length > 0) {
          this.nbtData = data;
          this.hasData = true;
          this.updateCurrentTrade();
        }
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  toggleRow(key: string): void {
    if (this.expandedStep === key) {
      this.expandedStep = null;
      this.weightChanges = [];
    } else {
      this.expandedStep = key;
      const row = this.tableRows.find((r) => r.key === key);
      if (row?.step) {
        this.weightChanges = this.getWeightChanges(row.step);
      }
    }
  }

  getExpandedStep(): TradeStep | null {
    const row = this.tableRows.find((r) => r.key === this.expandedStep);
    return row?.step ?? null;
  }

  getSharpeImprovement(): number {
    const step = this.getExpandedStep();
    const baseline = this.forwardBaselineSharpe;
    if (!step || baseline == null) return 0;
    return step.sharpe - baseline;
  }

  // Forward (mu/Sigma-based) baseline Sharpe from the NBT API payload
  get forwardBaselineSharpe(): number | null {
    const isFin = (v: any): boolean =>
      v != null && Number.isFinite(v);
    const fromCurrent = this.currentTrade?.baseline_sharpe;
    if (isFin(fromCurrent)) return fromCurrent;
    const fromAny = this.nbtData.find(
      (item) => isFin(item?.baseline_sharpe),
    )?.baseline_sharpe;
    if (isFin(fromAny)) return fromAny;
    const fallback = this.baselineMetrics?.sharpe;
    return isFin(fallback) ? fallback : null;
  }

  applyTrade(step: TradeStep): void {
    if (!this.portfolioId || !this.assetList?.length) return;

    const weights = step.new_weights.map((w, i) => ({
      weight: w,
      ticker: this.assetList[i]?.Ticker ?? `asset_${i}`,
      source: "next_best_trade",
    }));

    this.isSaving = true;
    this.portfolioService
      .applyNextBestTrade(this.portfolioId, { data: weights })
      .subscribe({
        next: () => {
          this.portfolioService.notificationService.open(
            "Weights saved, updating...",
          );
          // R needs ~2-3s for the quick recompute; 5s leaves headroom for the
          // 202-async case where the apply POST returns before R is done.
          // Refresh the portfolio object plus the optimizationData and
          // evaluationData service caches so every page (incl. the risk page)
          // sees the freshly written Portfolio='next_best_trade' rows in
          // optim_performance / risk_contribution / evaluation_optim_performance
          // / evaluation_table / diversification_effect. Without refreshing
          // the portfolio itself, this.portfolio.optim_performance keeps the
          // pre-NBT snapshot and evaluation.component.ts:setGeneralData crashes
          // when filtering by Portfolio === 'next_best_trade'.
          setTimeout(() => {
            forkJoin({
              portfolio: this.portfolioService.viewItem(this.portfolioId!),
              optimization: this.portfolioService.fetchOptimizationData(
                this.portfolioId!,
                true,
              ),
              evaluation: this.portfolioService.fetchEvaluationData(
                this.portfolioId!,
                true,
              ),
            }).subscribe({
              next: ({ portfolio, optimization }) => {
                if (portfolio) {
                  this.portfolioService.selectPortfolio(
                    new PortfolioModel(portfolio),
                  );
                }
                this.isSaving = false;
                this.customTradeSaved.emit({
                  step,
                  optimizationData: optimization,
                });
              },
              error: () => {
                this.isSaving = false;
                this.portfolioService.notificationService.open(
                  "Failed to refresh optimization data.",
                );
              },
            });
          }, 5000);
        },
        error: () => {
          this.isSaving = false;
          this.portfolioService.notificationService.open(
            "Failed to save weights",
          );
        },
      });
  }

  formatPercent(value: number | null): string {
    if (value == null || !Number.isFinite(value)) return "-";
    return (value * 100).toFixed(2) + "%";
  }

  formatSharpe(value: number | null): string {
    if (value == null || !Number.isFinite(value)) return "-";
    return value.toFixed(4);
  }

  private updateCurrentTrade(): void {
    this.currentTrade =
      this.nbtData.find((item) => item.id_w === this.sliderVal + 1) ??
      this.nbtData[this.sliderVal] ??
      null;
    this.expandedStep = null;
    this.weightChanges = [];
    this.buildTableRows();
  }

  private buildTableRows(): void {
    const rows: TableRow[] = [];
    const baselineSharpe = this.forwardBaselineSharpe;

    // baselineMetrics values are in percentage points (e.g. 11.46 means 11.46%)
    // Convert to decimals (0.1146) for consistency with step data from the API
    rows.push({
      key: "baseline",
      portfolio: "Baseline",
      trades: 0,
      expReturn:
        this.baselineMetrics?.expReturn != null
          ? this.baselineMetrics.expReturn / 100
          : null,
      risk:
        this.baselineMetrics?.risk != null
          ? this.baselineMetrics.risk / 100
          : null,
      sharpe: baselineSharpe,
      sharpeDelta: 0,
      gapClosed: 0,
      turnover: 0,
      isBaseline: true,
      isSelectedOptimal: false,
      step: null,
    });

    const finiteDelta = (a: any, b: any): number | null =>
      Number.isFinite(a) && Number.isFinite(b) ? a - b : null;

    // Per-step copy provided by the PO. The short label is always visible
    // inline in the table, the long tooltip surfaces on hover. Wording is
    // intentionally specific to the trade count (1 / 2 / 3) so the message
    // remains actionable.
    const emptyCopy: Record<
      "step_1" | "step_2" | "step_3",
      { short: string; tooltip: string }
    > = {
      step_1: {
        short: "No valid 1-trade optimization available",
        tooltip:
          "No single trade satisfies the selected portfolio constraints while improving the portfolio’s Sharpe ratio. Adjust the constraints, evaluate a multi-trade solution, or run a full rebalance.",
      },
      step_2: {
        short: "No valid 2-trade optimization available",
        tooltip:
          "No two-trade combination satisfies the selected portfolio constraints while improving the portfolio’s Sharpe ratio. Adjust the constraints, allow additional trades, or run a full rebalance.",
      },
      step_3: {
        short: "No valid 3-trade optimization available",
        tooltip:
          "No three-trade combination satisfies the selected portfolio constraints while improving the portfolio’s Sharpe ratio. Adjust the constraints or run a full rebalance.",
      },
    };

    if (this.currentTrade) {
      for (const def of this.stepDefs) {
        const step = this.currentTrade.trades[def.key];
        // An "empty step" -- R sometimes ships `{}` instead of NULL, or no
        // Sharpe-improving trade was found at this depth. Render a
        // placeholder row instead of skipping, so the user sees which
        // slot is empty (1-Trade / 2-Trade / 3-Trade) plus the PO copy
        // explaining why and what to do next. Click is disabled.
        const hasValidStep =
          !!step &&
          Array.isArray(step.new_weights) &&
          step.new_weights.length > 0;
        if (!hasValidStep) {
          const copy = emptyCopy[def.key];
          rows.push({
            key: def.key,
            portfolio: def.label,
            trades: def.trades,
            expReturn: null,
            risk: null,
            sharpe: null,
            sharpeDelta: null,
            gapClosed: null,
            turnover: null,
            isBaseline: false,
            isSelectedOptimal: false,
            step: null,
            isEmpty: true,
            emptyShortLabel: copy.short,
            emptyTooltip: copy.tooltip,
          });
          continue;
        }
        rows.push({
          key: def.key,
          portfolio: def.label,
          trades: def.trades,
          expReturn: step.exp_return,
          risk: step.risk,
          sharpe: step.sharpe,
          sharpeDelta: finiteDelta(step.sharpe, baselineSharpe),
          gapClosed: step.closeness,
          turnover: step.turnover,
          isBaseline: false,
          isSelectedOptimal: false,
          step,
        });
      }
    }

    if (this.selectedPortfolioMetrics) {
      const selSharpe = this.selectedPortfolioMetrics.sharpe;
      rows.push({
        key: "selected_optimal",
        portfolio: "Selected Optimal",
        trades: "Full rebalance",
        expReturn: this.selectedPortfolioMetrics.expReturn / 100,
        risk: this.selectedPortfolioMetrics.risk / 100,
        sharpe: selSharpe,
        sharpeDelta: finiteDelta(selSharpe, baselineSharpe),
        gapClosed: 1,
        turnover: null,
        isBaseline: false,
        isSelectedOptimal: true,
        step: null,
      });
    }

    this.tableRows = rows;
    this.emitTradePoints();
  }

  private emitTradePoints(): void {
    const points: TradePoint[] = this.tableRows
      .filter((r) => !r.isBaseline && !r.isSelectedOptimal && r.step)
      .map((r) => ({
        x: +(r.risk * 100).toFixed(2),
        y: +(r.expReturn * 100).toFixed(2),
        label: r.portfolio,
        sharpe: r.sharpe,
      }));
    this.tradePointsChanged.emit(points);
  }

  private getSecurityTicker(sec: any): string {
    if (sec?.fs_local_ticker) return sec.fs_local_ticker;
    if (sec?.fs_security_id) return sec.fs_security_id;
    if (sec?.FundShareClassId) return sec.FundShareClassId;
    return sec?.company_name ?? "";
  }

  private getWeightChanges(step: TradeStep): WeightChangeRow[] {
    if (!this.assetList?.length) return [];

    // Build a lookup of baseline portfolio allocations by ticker.
    // symbol_performance stores Weight as a decimal (0-1), same format as new_weights.
    const baselineByTicker: Record<string, number> = {};
    for (const e of this.portfolio?.symbol_performance ?? []) {
      if (e.Ticker !== "Portfolio" && e.Weight > 0) {
        baselineByTicker[e.Ticker] = e.Weight;
      }
    }

    const changes: WeightChangeRow[] = [];
    let unchangedCount = 0;

    // Defensive: if a step row somehow gets through with no new_weights
    // (stale data, manual click before fetchData completes), bail out
    // instead of crashing on undefined.forEach.
    if (!Array.isArray(step?.new_weights)) return changes;

    // assetList (optimWeights) is in the same positional order as new_weights
    step.new_weights.forEach((newW, i) => {
      const asset = this.assetList[i];
      if (!asset) return;
      const ticker = asset.Ticker;
      const name = asset.company_name ?? `Asset ${i}`;
      const before = baselineByTicker[ticker] ?? 0;
      const diff = newW - before;

      if (Math.abs(diff) >= 0.0001) {
        changes.push({ asset: name, before, after: newW, change: diff });
      } else {
        unchangedCount++;
      }
    });

    changes.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

    if (unchangedCount > 0) {
      changes.push({
        asset: `Other assets: unchanged (${unchangedCount})`,
        before: 0,
        after: 0,
        change: 0,
      });
    }

    return changes;
  }
}
